import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getHorsepower } from '@/api/public';
import RoundBadge from '@/components/Common/Badge/RoundBadge';
import DualFooterButton from '@/components/Common/Button/DualFooterButton';
import RadioList, { OptionType } from '@/components/Common/Input/Radio/RadioList';
import TextInputLabelOff from '@/components/Common/Input/TextInputLabelOff';
import TextInputLabelUp from '@/components/Common/Input/TextInputLabelUp';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import MenuHeader from '@/components/Header/MenuHeader';
import { ChevronDownIcon } from '@/components/Icon';
import ConfirmExitPopup from '@/components/Products/ConfirmExitPopup';
import { colors } from '@/const/colors';
import { ADDRESS1, ADDRESS2, ADDRESS3, SALESTYPE } from '@/const/products';
import { SELL_CAR_DEFAULT_TITLE } from '@/const/title';
import { useProductEnumContext } from '@/contexts/Products/ProductEnumContext';
import useUpdateProductFormMutation from '@/hooks/query/useUpdateProductFormMutation';
import useBlockNavigation from '@/hooks/useBlockNavigation';
import useExitConfirmationPopup from '@/hooks/useExitConfirmationPopup';
import useFetchProductData from '@/hooks/useFetchProductData';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { productsFormAtom, useResetProductsForm } from '@/store/products';
import { formatNumber } from '@/utils/common';
import {
  processDistanceInput,
  processPowerInput,
  processTonnageInput,
  validateDistance,
  validatePower,
} from '@/utils/validation';

const keyValueList: KeyValueListType = {
  color: '색상',
  transmission: '변속기',
  fuel: '연료',
  garage: '차고지',
  area1: '활동지',
  area2: '지역',
  area3: '지역',
  salesType: '판매 방식 변경',
};

type SalesTypeKey = keyof typeof SALESTYPE;

const AdditionalInfoForm = () => {
  const hasPowerValue = (value: string | number | null | undefined) =>
    value !== '' && value !== null && value !== undefined;

  /** 외부 마력 목록과 숫자/문자열 타입 차이 없이 매칭 */
  const powerMatchesHorsepowerList = (saved: string | number | null | undefined, horsepower: unknown[]) => {
    if (!hasPowerValue(saved)) return false;
    const n = Number(saved);
    const s = String(saved).trim();
    if (s === '' || Number.isNaN(n)) return false;
    return horsepower.some((hp) => {
      const hs = String(hp).trim();
      return hs === s || Number(hp) === n;
    });
  };

  const { productEnum } = useProductEnumContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const numericProductId = id ? Number(id) : 0;

  const [productFormData] = useAtom(productsFormAtom);
  const setProductFormData = useSetAtom(productsFormAtom);
  const resetProductsForm = useResetProductsForm();
  const [prevData, setPrevData] = useState<ProductDetailResponse | null>(null);

  const distanceRef = useRef<HTMLInputElement>(null);
  const fuelRef = useRef<HTMLInputElement>(null);
  const powerRef = useRef<HTMLInputElement>(null);
  const colorRef = useRef<HTMLInputElement>(null);
  const garageRef = useRef<HTMLInputElement>(null);
  // const activityRef = useRef<HTMLInputElement>(null);
  const area1ListRef = useRef<HTMLInputElement>(null);
  const area2ListRef = useRef<HTMLInputElement>(null);
  const area3ListRef = useRef<HTMLInputElement>(null);

  const area1List = ADDRESS1;
  const area2List = ADDRESS2[productFormData?.area1 || ''] || [];
  const area3List = ADDRESS3[productFormData?.area1 || ''] || [];

  const checkForChanges = () => {
    return !!getChangedData;
  };

  const saveChanges = () => {
    const changedData = getChangedData();
    if (Object.keys(changedData).length > 0) {
      const request = { id: Number(productFormData?.id), ...changedData };
      updateProductTemporarySaveMutation.mutate(request);
    }
  };

  const { isShowExitConfirmPopup, openExitConfirmPopup, closeExitConfirmPopup, confirmExit } = useExitConfirmationPopup(
    checkForChanges,
    saveChanges,
    resetProductsForm,
  );

  useBlockNavigation(openExitConfirmPopup);

  const [isShow, setIsShow] = useState(false);
  const [title, setTitle] = useState('');
  const [optionData, setOptionData] = useState<OptionType[]>([]);

  const [distanceError, setDistanceError] = useState(false);
  const [distanceErrorMsg, setDistanceErrorMsg] = useState('');
  const [powerError, setPowerError] = useState(false);
  const [powerErrorMsg, setPowerErrorMsg] = useState('');
  const [isShowHorsepowerModal, setIsShowHorsepowerModal] = useState(false);
  const [horsepowerOptions, setHorsepowerOptions] = useState<OptionType[]>([]);
  const [isLoadingHorsepower, setIsLoadingHorsepower] = useState(false);
  const [isDirectInputMode, setIsDirectInputMode] = useState(false); // 마력수 직접 입력 모드
  const [isEditingPower, setIsEditingPower] = useState(false); // 마력수 입력 중 여부

  // URL·atom 불일치 시 또는 매물인데 마력이 비어 있을 때(중간 단계에서 누락된 경우) 서버에서 다시 로드
  // 단, 사용자가 마력을 직접 입력 중일 때는(api 호출 방지) power 비어 있어도 재조회하지 않음
  useFetchProductData(
    id,
    numericProductId > 0 &&
      (productFormData.id !== numericProductId || (!hasPowerValue(productFormData.power) && !isEditingPower)),
  );

  useEffect(() => {
    setPrevData(productFormData);
  }, []);

  // 마력수 값 검증 및 자동 설정 함수
  const validatePowerWithApi = async () => {
    // 사용자가 마력을 직접 입력 중일 때는 API 호출하지 않음
    if (isEditingPower) return;

    const manufacturer = productFormData?.manufacturerCategories?.name;
    const modelName = productFormData?.model?.name;
    const tons = productFormData?.tons;
    const power = productFormData?.power;

    // 제조사/모델/톤수 또는 마력 값이 없으면 API 호출 불필요
    // (사용자가 직접 마력을 지워서 빈값이 된 경우 등)
    if (!manufacturer || !modelName || !tons || !hasPowerValue(power)) return;

    const validationKey = `${manufacturer}-${modelName}-${tons}`;
    const sessionKey = `productPowerValidationKey:${numericProductId || 'new'}`;

    const lastValidationKey = sessionStorage.getItem(sessionKey);

    const isChangedCombination = lastValidationKey !== null && lastValidationKey !== validationKey;

    try {
      const response = await getHorsepower({
        manufacturer,
        modelName,
        tons: String(tons),
        horsePower: String(power ?? ''),
      });

      if (response?.result === 'Y' && Array.isArray(response.horsepower)) {
        if (response.horsepower.length === 0) {
          setIsDirectInputMode(true);
          if (isChangedCombination) {
            setProductFormData((prev) => ({ ...prev, power: '' }));
            setPowerError(true);
            setPowerErrorMsg('마력을 입력해주세요.');
          } else if (!hasPowerValue(productFormData?.power)) {
            setPowerError(true);
            setPowerErrorMsg('마력을 입력해주세요.');
          } else {
            setPowerError(false);
            setPowerErrorMsg('');
          }
        } else {
          const savedPower = productFormData?.power;
          if (powerMatchesHorsepowerList(savedPower, response.horsepower)) {
            setIsDirectInputMode(false);
            setPowerError(false);
            setPowerErrorMsg('');
          } else if (hasPowerValue(savedPower)) {
            // 서버/기존에 저장된 마력이 외부 목록에 없어도 값은 유지 (직접입력 모드)
            setIsDirectInputMode(true);
            const tonsNorm = processTonnageInput(productFormData?.tons ?? '0', 1);
            const { isValid, errorMessage } = validatePower(String(savedPower), tonsNorm);
            setPowerError(!isValid);
            setPowerErrorMsg(isValid ? '' : errorMessage);
          } else {
            setIsDirectInputMode(false);
            setPowerError(true);
            setPowerErrorMsg('마력을 선택해주세요.');
          }
        }
      }
    } catch (error) {
      console.error('마력수 검증 실패:', error);
      setIsDirectInputMode(true);
      if (!hasPowerValue(productFormData?.power)) {
        setPowerError(true);
        setPowerErrorMsg('마력을 입력해주세요.');
      }
    } finally {
      sessionStorage.setItem(sessionKey, validationKey);
    }
  };

  useEffect(() => {
    validatePowerWithApi();
  }, [
    productFormData?.manufacturerCategories?.name,
    productFormData?.model?.name,
    productFormData?.tons,
    isEditingPower,
  ]);

  const updateProductTemporarySaveMutation = useUpdateProductFormMutation({});

  const getChangedData = () => {
    const changedData: Partial<ProductRegisterRequest> = {};
    const keysToCheck: Array<keyof ProductRegisterRequest> = [
      'transmission',
      'distance',
      'fuel',
      'power',
      'color',
      'garage',
      'area1',
      'area2',
      'area3',
    ];
    keysToCheck.forEach((key) => {
      // area1, area2, area3는 문자열로 저장되므로 직접 비교
      if (key === 'area1' || key === 'area2' || key === 'area3') {
        if (prevData?.[key] !== productFormData?.[key]) {
          changedData[key] = productFormData[key] || '';
        }
      } else if (key === 'power') {
        if (prevData?.[key] !== productFormData?.[key]) {
          changedData[key] = productFormData[key] || '';
        }
      } else {
        // 다른 필드는 객체이므로 code 비교
        if (prevData?.[key]?.code !== productFormData?.[key]?.code || prevData?.[key] !== productFormData?.[key]) {
          changedData[key] = productFormData[key]?.code || productFormData[key];
        }
      }
    });

    // 유효성 검사: area1과 상관관계가 없는 area2/area3는 null로 전송
    const selectedArea1 = productFormData?.area1 || '';
    const validArea2List = ADDRESS2[selectedArea1]?.map((x) => x.desc) || [];
    const validArea3List = ADDRESS3[selectedArea1]?.map((x) => x.desc) || [];

    // area1이 변경되었다면 해당하는 area2/area3 값을 검증하여 포함
    if (prevData?.area1 !== productFormData?.area1) {
      const nextArea2 = productFormData?.area2;
      const nextArea3 = productFormData?.area3;

      (changedData as any).area2 = nextArea2 && validArea2List.includes(nextArea2) ? nextArea2 : '';
      (changedData as any).area3 = nextArea3 && validArea3List.includes(nextArea3) ? nextArea3 : '';
    } else {
      if (productFormData?.area2 && !validArea2List.includes(productFormData.area2)) {
        (changedData as any).area2 = '';
      }
      if (productFormData?.area3 && !validArea3List.includes(productFormData.area3)) {
        (changedData as any).area3 = '';
      }
    }

    return changedData;
  };

  const updateProductNextStepMutation = useUpdateProductFormMutation({
    url: `/products/sales/price-trend/${id}`,
  });

  const handleClickNext = () => {
    const changedData: Partial<ProductRegisterRequest> = getChangedData();
    if (Object.keys(changedData).length > 0) {
      const request = { id: Number(productFormData?.id), ...changedData };
      updateProductNextStepMutation.mutate(request);
    } else {
      navigate(`/products/sales/price-trend/${id}`, { replace: true });
    }
  };

  const handleClickPrev = () => {
    navigate(`/products/sales/axis/${id}`, { replace: true });
  };

  const handleClickSelectBox = (key: string) => {
    setTitle(key);
    if (key === 'area1') {
      setOptionData(ADDRESS1.map((item) => ({ code: item.desc, desc: item.desc })) as OptionType[]);
    } else if (key === 'area2') {
      const area1Desc = productFormData?.area1 ?? '';
      const area2Options = ADDRESS2[area1Desc] || [];
      const area3SelectedDesc = productFormData?.area3;
      const filteredOptions = area3SelectedDesc
        ? area2Options.filter((item) => item.desc !== area3SelectedDesc)
        : area2Options;
      setOptionData(filteredOptions.map((item) => ({ code: item.desc, desc: item.desc })) as OptionType[]);
    } else if (key === 'area3') {
      const area1Desc = productFormData?.area1 ?? '';
      const area3Options = ADDRESS3[area1Desc] || [];
      const area2SelectedDesc = productFormData?.area2;
      const filteredOptions = area2SelectedDesc
        ? area3Options.filter((item) => item.desc !== area2SelectedDesc)
        : area3Options;
      setOptionData(filteredOptions.map((item) => ({ code: item.desc, desc: item.desc })) as OptionType[]);
    } else {
      if (!productEnum) return;
      setOptionData(productEnum[key] as OptionType[]);
    }
    setIsShow(true);
  };

  const handleSelectOption = (item: OptionType, key: string) => {
    if (key === 'transmission' && distanceRef && distanceRef.current) {
      distanceRef.current.focus();
    }

    if (key === 'area1') {
      setProductFormData({
        ...productFormData,
        area1: item.desc,
        area2: '',
        area3: '',
      } as ProductDetailResponse);
    } else if (key === 'area2') {
      setProductFormData({
        ...productFormData,
        area2: item.desc,
        ...(productFormData?.area3 === item.desc && { area3: '' }),
      } as ProductDetailResponse);
    } else if (key === 'area3') {
      setProductFormData({
        ...productFormData,
        area3: item.desc,
        ...(productFormData?.area2 === item.desc && { area2: '' }),
      } as ProductDetailResponse);
    } else {
      setProductFormData({ ...productFormData, [key]: item } as ProductDetailResponse);
    }

    setTimeout(() => {
      setIsShow(false);
    }, 200);
  };

  const handleChangeDistanceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const trimmedValue = processDistanceInput(value);
    const { isValid, errorMessage } = validateDistance(trimmedValue);

    setProductFormData({ ...productFormData, [name]: trimmedValue } as ProductDetailResponse);

    if (!isValid) {
      setDistanceError(true);
      setDistanceErrorMsg(errorMessage);
    } else {
      setDistanceError(false);
      setDistanceErrorMsg('');
    }
  };

  const handleChangePowerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const trimmedValue = processPowerInput(value);

    setProductFormData({ ...productFormData, [name]: trimmedValue } as ProductDetailResponse);

    if (trimmedValue === '') {
      setPowerError(true);
      setPowerErrorMsg('마력을 입력해주세요.');
      return;
    }
    const tons = processTonnageInput(productFormData?.tons ?? '0', 1);
    const { isValid, errorMessage } = validatePower(trimmedValue, tons);
    if (!isValid) {
      setPowerError(true);
      setPowerErrorMsg(errorMessage);
    } else {
      setPowerError(false);
      setPowerErrorMsg('');
    }
  };

  const fetchHorsepower = async () => {
    const manufacturer = productFormData?.manufacturerCategories?.name;
    const modelName = productFormData?.model?.name;
    const tons = productFormData?.tons;
    const horsePower = productFormData?.power || '';
    // 파라미터 체크
    if (!manufacturer || !modelName || !tons) {
      setIsShowHorsepowerModal(true);
      return;
    }

    setIsLoadingHorsepower(true);
    try {
      const response = await getHorsepower({
        manufacturer,
        modelName,
        tons: String(tons),
        horsePower,
      });

      if (response?.result === 'Y' && Array.isArray(response.horsepower)) {
        if (response.horsepower.length === 0) {
          setIsDirectInputMode(true);
        } else {
          setIsDirectInputMode(false);
          const options: OptionType[] = response.horsepower.map((hp: number) => ({
            code: String(hp),
            desc: String(hp),
          }));
          setHorsepowerOptions(options);
          setTitle('power');
          setIsShow(true);
        }
      } else {
        setIsDirectInputMode(true);
      }
    } catch (error) {
      console.error('마력수 조회 실패:', error);
      setIsDirectInputMode(true);
    } finally {
      setIsLoadingHorsepower(false);
    }
  };

  const handleClickPowerSelectBox = () => {
    fetchHorsepower();
  };

  const handleChangeRadioInput = (newVal: OptionType, key: string) => {
    if (key === 'transmission' && distanceRef && distanceRef.current) {
      distanceRef.current.focus();
    }
    setProductFormData({ ...productFormData, [key]: newVal } as ProductDetailResponse);
  };

  // 1톤 여부에 따른 활동지 노출 제어
  const isOneTon =
    Number(productFormData?.tons) === 1 ||
    Number(productFormData?.tons) === 1.1 ||
    Number(productFormData?.tons) === 1.2;

  const scrollIntoView = (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e) {
      const { name } = e.target;
      if (name === 'distance') {
        distanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'power') {
        powerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  };

  useLayoutEffect(() => {
    const detectMobileKeyboard = () => {
      scrollIntoView();
    };
    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  });

  // power가 없을 때 에러 메시지 설정
  useEffect(() => {
    if (!hasPowerValue(productFormData?.power)) {
      const errorMsg = isDirectInputMode ? '마력을 입력해주세요.' : '마력을 선택해주세요.';
      if (!powerError || powerErrorMsg !== errorMsg) {
        setPowerError(true);
        setPowerErrorMsg(errorMsg);
      }
    } else {
      if (powerError) {
        setPowerError(false);
        setPowerErrorMsg('');
      }
    }
  }, [productFormData?.power, powerError, powerErrorMsg, isDirectInputMode]);

  const isDisabledNextBtn = () => {
    return (
      !productFormData?.transmission?.code ||
      !productFormData?.distance ||
      !productFormData?.fuel?.code ||
      !hasPowerValue(productFormData?.power) ||
      distanceError ||
      powerError
    );
  };

  return (
    <>
      <DefaultHelmet page="sale" />

      <MenuHeader title={SALESTYPE[productFormData.type?.code as SalesTypeKey] || SELL_CAR_DEFAULT_TITLE} />

      <div className="flex flex-col w-full pt-[60px] p-4">
        <div className="py-4 pb-24 my-2">
          <div className="relative flex justify-between">
            <h3 className="text-2xl font-bold text-gray-8 break-keep">
              차량의 추가 정보를
              <br />
              입력해주세요.
            </h3>
            <RoundBadge text="5/9" />
          </div>

          <div className="flex flex-col gap-[30px] pt-[30px]">
            <div>
              <label className="mb-3 text-base font-medium text-gray-8">
                변속기<span className="font-normal text-red">(필수)</span>
              </label>
              <div className="px-1">
                <RadioList
                  name="transmission"
                  list={(productEnum?.transmission.filter((item) => item.code !== 'BOTH') as OptionType[]) || []}
                  value={productFormData?.transmission?.code || ''}
                  onChange={(val) => {
                    handleChangeRadioInput(val, 'transmission');
                  }}
                  justify="equal"
                />
              </div>
            </div>
            <TextInputLabelUp
              ref={distanceRef}
              name="distance"
              label="주행거리"
              placeholder="주행거리 입력"
              value={formatNumber(productFormData?.distance?.toString(), true) || ''}
              onChange={(e) => handleChangeDistanceInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              inputMode="numeric"
              required
              error={distanceError}
              errorMsg={distanceErrorMsg}
              suffix="km"
              maxLength={8}
            />

            <div onClick={!isDirectInputMode ? handleClickPowerSelectBox : undefined}>
              <TextInputLabelUp
                ref={powerRef}
                name="power"
                label="마력수"
                placeholder={isDirectInputMode ? '마력수 입력' : '마력수 선택'}
                value={productFormData?.power || ''}
                type="text"
                inputMode={isDirectInputMode ? 'numeric' : undefined}
                readOnly={!isDirectInputMode}
                onChange={isDirectInputMode ? handleChangePowerInput : undefined}
                onFocus={
                  isDirectInputMode
                    ? (e) => {
                        setIsEditingPower(true);
                        scrollIntoView(e);
                      }
                    : undefined
                }
                onBlur={isDirectInputMode ? () => setIsEditingPower(false) : undefined}
                suffix={isDirectInputMode ? 'PS' : <ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
                error={powerError}
                errorMsg={powerErrorMsg}
                maxLength={isDirectInputMode ? 4 : undefined}
              />
            </div>

            <div onClick={() => handleClickSelectBox('fuel')}>
              <TextInputLabelUp
                ref={fuelRef}
                name="fuel"
                label="연료"
                placeholder="연료 선택"
                value={productFormData?.fuel?.desc || ''}
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
              />
            </div>

            <div onClick={() => handleClickSelectBox('color')}>
              <TextInputLabelUp
                ref={colorRef}
                name="color"
                label="색상"
                placeholder="색상 선택"
                value={productFormData?.color?.desc || ''}
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
              />
            </div>

            <div onClick={() => handleClickSelectBox('garage')}>
              <TextInputLabelUp
                ref={garageRef}
                name="garage"
                label="차고지"
                placeholder="차고지 선택"
                value={productFormData?.garage?.desc || ''}
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
              />
            </div>
            {isOneTon && (
              <>
                <div onClick={() => handleClickSelectBox('area1')}>
                  <TextInputLabelUp
                    ref={area1ListRef}
                    name="area1"
                    label="활동지"
                    placeholder="활동지 선택"
                    value={productFormData?.area1 || ''}
                    type="text"
                    readOnly
                    suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                  />
                </div>

                {area2List.length > 1 && (
                  <>
                    <div onClick={() => handleClickSelectBox('area2')}>
                      <TextInputLabelOff
                        ref={area2ListRef}
                        name="area2"
                        placeholder="지역 선택"
                        fontSize={18}
                        value={productFormData?.area2 || ''}
                        readOnly
                        suffix={<ChevronDownIcon color={colors.gray[8]} />}
                      />
                    </div>
                    <div onClick={() => handleClickSelectBox('area3')}>
                      <TextInputLabelOff
                        ref={area3ListRef}
                        name="area3"
                        placeholder="지역 선택"
                        fontSize={18}
                        value={productFormData?.area3 || ''}
                        readOnly
                        suffix={<ChevronDownIcon color={colors.gray[8]} />}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <BottomSheetPopup isOpen={isShow} onClose={() => setIsShow(false)} title={keyValueList[title] || '마력수'}>
          <div className="px-4 pb-8">
            {isLoadingHorsepower ? (
              <div className="py-8 text-center text-gray-6">로딩 중...</div>
            ) : (
              <RadioList
                name={title}
                list={title === 'power' ? horsepowerOptions : optionData}
                value={
                  title === 'power'
                    ? productFormData?.power || ''
                    : title === 'area1' || title === 'area2' || title === 'area3'
                    ? productFormData?.[title] || '' // desc 값이 code로도 사용됨
                    : productFormData?.[title]?.code || ''
                }
                onChange={(val) => {
                  if (title === 'power') {
                    const selectedPower = val.code;
                    setProductFormData({ ...productFormData, power: selectedPower } as ProductDetailResponse);

                    // 0을 선택한 경우는 유효성 검사 통과
                    if (selectedPower === '0') {
                      setPowerError(false);
                      setPowerErrorMsg('');
                    } else {
                      const tons = processTonnageInput(productFormData?.tons ?? '0', 1);
                      const { isValid, errorMessage } = validatePower(selectedPower, tons);
                      if (!isValid) {
                        setPowerError(true);
                        setPowerErrorMsg(errorMessage);
                      } else {
                        setPowerError(false);
                        setPowerErrorMsg('');
                      }
                    }
                    setTimeout(() => {
                      setIsShow(false);
                    }, 200);
                  } else {
                    handleSelectOption(val, title);
                  }
                }}
                horizontal={false}
              />
            )}
          </div>
        </BottomSheetPopup>
        <DualFooterButton
          leftButtonText="이전"
          onClickLeftButton={handleClickPrev}
          rightButtonText="시세 조회하기"
          disabledRight={isDisabledNextBtn()}
          onClickRightButton={handleClickNext}
        />
      </div>
      <ConfirmExitPopup isShow={isShowExitConfirmPopup} onClose={closeExitConfirmPopup} onConfirm={confirmExit} />
    </>
  );
};

export default AdditionalInfoForm;
